/**
 * A country's name in the reader's language — the ONE tool every portal uses.
 * (F34)
 *
 * The product printed country names from `@tonnie/foundation`'s ENGLISH table
 * (`getCountryName`, `COUNTRIES[].name`): a pt-PT reader's journey selector,
 * billing card, settings and every country picker said "Brazil". Names live in
 * the catalogue as `common.countries.<ALPHA-2>` and are reached here through
 * `COUNTRY_NAME_KEY` — a typed map of literal keys that
 * `dynamic-key-coverage.spec.ts` ties to `COUNTRIES` — never a key built at a
 * call site. The lint config bans the English lookups in the apps.
 *
 * `null`, `''` and the destination sentinel `UNK` are "not chosen yet", not a
 * country, and read as `common.unknown`. A real ISO code the product does not
 * offer (a Pool import may store any) has no name to translate and renders as
 * its ISO code, as `getCountryName` always did.
 */
import { useCallback } from 'react';
import { isKnownDestination } from '@tonnie/foundation';

import { useTranslation } from './useTranslation';
import { countryNameKey } from '../utils/country-name-keys';

export type CountryNameFn = (code: string | null | undefined) => string;

export function useCountryName(): CountryNameFn {
  const { t } = useTranslation();
  return useCallback<CountryNameFn>(
    (code) => {
      if (!isKnownDestination(code)) return t('common.unknown');
      const key = countryNameKey(code);
      return key === null ? code.toUpperCase() : t(key);
    },
    [t]
  );
}
